import * as React from 'react';

import classnames from 'classnames';

import { Grid } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { buildDegreeUrlRelative } from 'bundles/common/utils/urlUtils';
import { snakeCaseNavigationName } from 'bundles/degree-description/utils/utils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import type { NavigationElementFieldsFragment } from 'bundles/xddp/__generated__/contentfulTypes';
import Container from 'bundles/xddp/components/shared/Container';
import type { NavigationProps, UserAgentProps } from 'bundles/xddp/types';

import 'css!bundles/xddp/components/__styles__/shared/Navigation';

const Navigation = ({
  navigationElements,
  currentNavElement,
  slug,
  isFixed,
  isHeaderPresent,
}: NavigationProps & UserAgentProps) => {
  const track = useTracker();

  const getNavDOMElementByIndex = (i: number) => document.getElementById(`xddp-nav-title-${i}`);

  const checkIfElementStartsWithKey = (element: NavigationElementFieldsFragment, key: string) =>
    element?.label?.toLowerCase().startsWith(key.toLowerCase());
  // use the keyboard controls from https://www.w3.org/TR/wai-aria-practices/examples/menubar/menubar-1/menubar-1.html
  const getKeyDownHandlerForIndex = (index: number) => (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    switch (event.key) {
      // focus next menu item
      case 'ArrowRight': {
        const nextIndex = index === navigationElements.length - 1 ? 0 : index + 1;
        const nextNavElement = getNavDOMElementByIndex(nextIndex);
        nextNavElement?.focus();
        break;
      }
      // focus previous menu item
      case 'ArrowLeft': {
        const prevIndex = index === 0 ? navigationElements.length - 1 : index - 1;
        const prevNavElement = getNavDOMElementByIndex(prevIndex);
        prevNavElement?.focus();
        break;
      }
      // focus first menu item
      case 'Home': {
        event.preventDefault();
        const firstNavElement = getNavDOMElementByIndex(0);
        firstNavElement?.focus();
        break;
      }
      // focus last menu item
      case 'End': {
        event.preventDefault();
        const firstNavElement = getNavDOMElementByIndex(navigationElements.length - 1);
        firstNavElement?.focus();
        break;
      }
      // if the key is a character search for the next menu item that starts with the character and focus it
      default: {
        if (event.key.length === 1) {
          // search forward from index
          let searchedIndex = navigationElements.findIndex(
            (element, searchIndex) => searchIndex > index && checkIfElementStartsWithKey(element, event.key)
          );
          if (searchedIndex === -1) {
            // search backward from index if there are none forward
            searchedIndex = navigationElements.findIndex(
              (element, searchIndex) => searchIndex < index && checkIfElementStartsWithKey(element, event.key)
            );
          }
          if (searchedIndex !== -1) {
            const searchedElement = document.getElementById(`xddp-nav-title-${searchedIndex}`);
            searchedElement?.focus();
          }
        }
      }
    }
  };

  const onClick = (index: number) => () => {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const clickedNavElementId = navigationElements[index].id;
    if (!clickedNavElementId) return;
    track('click_page_tab', {
      pageTab: { name: snakeCaseNavigationName(clickedNavElementId) },
    });
  };

  if (!navigationElements || !navigationElements.length) {
    return null;
  }

  const classNames = classnames('rc-Navigation', {
    'navigation-fixed': isFixed,
    'nav-header': isHeaderPresent,
  });

  return (
    <div className={classNames}>
      <Container>
        <Grid component="nav" className="nav-container">
          <ul>
            {navigationElements.map((navigationElement, index) => {
              const linkHref = navigationElement.isIndexPage ? '' : `/${navigationElement.route}`;

              const selectorClasses = classnames('nav-element', {
                'nav-selected': currentNavElement && navigationElement.id === currentNavElement.id,
              });

              const isCurrentElement = navigationElement.id === currentNavElement?.id;

              return (
                <li
                  className={selectorClasses}
                  key={navigationElement.id}
                  aria-current={isCurrentElement ? 'page' : 'false'}
                >
                  <TrackedLink2
                    className="nav-link"
                    id={`xddp-nav-title-${index}`}
                    trackingName="navigation_title"
                    href={{
                      pathname: buildDegreeUrlRelative(slug, linkHref),
                    }}
                    onKeyDown={getKeyDownHandlerForIndex(index)}
                    onClick={onClick(index)}
                  >
                    <span className="nav-label">{navigationElement.label}</span>
                  </TrackedLink2>
                </li>
              );
            })}
          </ul>
        </Grid>
      </Container>
    </div>
  );
};

export default Navigation;
